/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  PreConstructionWorksheetDto,
  PreConstructionWorksheetSaveDto,
  PreConstructionWorksheetScheduleTaskDto,
  PreConstructionWorksheetScheduleTemplateTaskDto,
  UserDetailsShortDto,
} from '@gh/builder-shared-data';
import {
  createArrayHelper,
  DateTimeTypes,
  EntityConfig,
  FieldConfig,
  isDirty,
  setEntityField,
  setEntityFieldWithDirty,
  setNestedEntityField,
} from '@gh/common-shared';
import { EMPTY_ID, EntityVersion, ID, WithDirty, WithEntityState, WithVersion } from '@gh/common-shared-data';
import { isNotNil } from '@gh/core-util';
import { filter, forEach, isEmpty, isNil, keyBy, max, omit, pick, range, sortBy, values } from 'lodash';
import { ScheduleTemplateListItem } from './schedule-list.model';

export type PreConstructionWorksheetInit = {
  nominatedTemplate: ScheduleTemplateListItem;
};

export type PreConstructionWorksheetTemplateTask = PreConstructionWorksheetScheduleTemplateTaskDto;
export type PreConstructionWorksheetSchedule = WithEntityState<WithDirty<WithVersion<{
  scheduleId: ID;
  jobId: ID;
  jobAdmin?: UserDetailsShortDto;
  floorArea: number;
  jobNumber: string;
  stageName: string;
  jobVersion: EntityVersion;
  siteAddress: string;
  customerName: string;
  estimatorName: string;
  contractAmount: number;
  rfqProducedDate?: Date;
  salespersonId: string;
  salespersonName: string;
  rfqAuthoritySignedDate?: Date;
  isSignedByEversign: boolean;
  isAuthToCommenceDateSet: boolean;
  isDpp: boolean;
  isFinalized: boolean;
  isNotProceeding: boolean;
  slabDate: Date;
  scheduleTasks: Hash<WithDirty<PreConstructionWorksheetScheduleTaskDto>>;
  // this flag is set when no one job can be edited due to max available job validation
  isEditable: boolean;
}>>>;
export type PreConstructionWorksheetFilter = {
  showWithSlabDateOnly: boolean;
  jobAdminIds: ID[];
  salespersonIds: ID[];
};
export type PreConstructionWorksheetJobAdmin = UserDetailsShortDto;

export type PreConstructionWorksheetDetails = {
  templateId: ID;
  filter: PreConstructionWorksheetFilter;
  templateTasks: PreConstructionWorksheetTemplateTask[];
  schedules: PreConstructionWorksheetSchedule[];
  originalSchedules: PreConstructionWorksheetSchedule[];
};

export const PRE_CONSTRUCTION_WORKSHEET_MAX_EDITED_SCHEDULES = 10;

const PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_FILTER_INIT: PreConstructionWorksheetFilter = {
  showWithSlabDateOnly: false,
  jobAdminIds: [],
  salespersonIds: [],
};

export const PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_DETAILS_INIT: PreConstructionWorksheetDetails = {
  templateId: EMPTY_ID,
  filter: PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_FILTER_INIT,
  schedules: [],
  originalSchedules: [],
  templateTasks: [],
};

export const preConstructionWorksheetDetailsFromDto = (dto: PreConstructionWorksheetDto,
                                                       templateId: ID): PreConstructionWorksheetDetails => {
  const { templateTasks } = dto;

  const schedules = dto.schedules.map((schedule) => <PreConstructionWorksheetSchedule>({
    ...omit(schedule, 'scheduleTasks'),
    isEditable: true,
    scheduleTasks: keyBy(schedule.scheduleTasks, 'templateTaskId'),
  }));

  return {
    templateId,
    filter: PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_FILTER_INIT,
    templateTasks,
    schedules,
    originalSchedules: schedules,
  };
};

export const preConstructionWorksheetDetailsToSaveDto = (details: PreConstructionWorksheetDetails): PreConstructionWorksheetSaveDto => ({
  templateId: details.templateId,
  schedules: details.schedules.filter(isDirty).map((schedule) => ({
    ...pick(schedule, 'scheduleId', 'jobId', 'rfqAuthoritySignedDate', 'version', 'jobAdmin', 'jobVersion'),
    scheduleTasks: values(schedule.scheduleTasks)
      .filter(isDirty)
      .map((task) => pick(task, 'scheduleTaskId', 'completionDate')),
  })),
});

export const filterPreConstructionWorksheetSchedules = (schedules: PreConstructionWorksheetSchedule[],
                                                        wFilter: PreConstructionWorksheetFilter) => {
  if (isEmpty(wFilter.salespersonIds) && isEmpty(wFilter.jobAdminIds) && !wFilter.showWithSlabDateOnly) {
    return schedules;
  } else {
    const salespersonIdIndex = keyBy(wFilter.salespersonIds);
    const jobAdminIdIndex = keyBy(wFilter.jobAdminIds);

    const hasSalespersonConstraint = !isEmpty(wFilter.salespersonIds);
    const hasJobAdminConstraint = !isEmpty(wFilter.jobAdminIds);

    return schedules.filter(({ salespersonId, jobAdmin, slabDate }) =>
      (!hasSalespersonConstraint || salespersonIdIndex.hasOwnProperty(salespersonId || EMPTY_ID))
      && (!hasJobAdminConstraint || jobAdminIdIndex.hasOwnProperty(jobAdmin && jobAdmin.userId || EMPTY_ID))
      && (!wFilter.showWithSlabDateOnly || isNotNil(slabDate)));
  }
};

export const getPreConstructionWorksheetScheduleId = (schedule: PreConstructionWorksheetSchedule) => schedule.jobId;

const isJobEditable = ({ isFinalized, isNotProceeding }: PreConstructionWorksheetSchedule) => !(isFinalized || isNotProceeding);

export const PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_BASE_ENTITY_CONFIG: EntityConfig<PreConstructionWorksheetSchedule> = {
  jobAdmin: {
    editable: (schedule) => schedule.isEditable && isJobEditable(schedule),
  },
  rfqAuthoritySignedDate: {
    editable: (schedule) => schedule.isEditable && !schedule.isSignedByEversign && isJobEditable(schedule),
    type: DateTimeTypes.ShortDate,
  },
};

export const PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_TASK_DATE_FIELD_CONFIG: FieldConfig<Date> = {
  editable: (schedule: PreConstructionWorksheetSchedule, name: string) => {
    const taskId = getPreConstructionWorksheetTaskIdFromName(name);
    return schedule.isEditable && isJobEditable(schedule)
      && (isNil(taskId) || isNotNil(schedule.scheduleTasks[taskId]));
  },
  type: DateTimeTypes.ShortDate,
};

const TASKS_NAME_RE = /^tasks\.([a-zA-Z0-9]+)$/;

export const getPreConstructionWorksheetTaskIdFromName = (field: string) => {
  const match = TASKS_NAME_RE.exec(field);
  return match && match[1];
};

export const generatePreConstructionWorksheetTaskFieldName = (id: ID) => `tasks.${id}`;

export const preConstructionWorksheetScheduleArr = createArrayHelper(getPreConstructionWorksheetScheduleId);

export type PreConstructionWorksheetTaskTreeNode = {
  task: PreConstructionWorksheetTemplateTask;
  children: PreConstructionWorksheetTaskTreeNode[];
  last: boolean;
  virtual: boolean;
  parent: boolean;
};

export const buildPreConstructionWorksheetTaskTree = (tasks: PreConstructionWorksheetTemplateTask[]): PreConstructionWorksheetTaskTreeNode[] => {
  const nodes: Hash<PreConstructionWorksheetTaskTreeNode> = {};

  tasks.forEach((task) => {
    nodes[task.taskNumber] = { task, children: [], last: false, parent: false, virtual: false };
  });

  tasks.forEach(({ parentTaskNumber, taskNumber }) => {
    if (parentTaskNumber) {
      const parentNode = nodes[parentTaskNumber];
      parentNode.parent = true;
      parentNode.children.push(nodes[taskNumber]);
    }
  });

  forEach(nodes, (node) => {
    node.children = sortBy(node.children, ({ task }) => task.taskNumber);
    node.children.forEach((child, i, children) => {
      const nextChild = children[i + 1];
      // mark this child as last if it is the last child, it is a parent or the next one child is a parent
      if (!nextChild || !isEmpty(child.children) || !isEmpty(nextChild.children)) {
        child.last = true;
      }
    });
  });

  const roots = filter(nodes, ({ task: { parentTaskNumber } }) => isNil(parentTaskNumber));

  roots.forEach((root) => {
    root.last = true;
  });

  return sortBy(roots, ({ task }) => task.taskNumber);
};

export const getPreConstructionWorksheetTaskTreeMaxDepth = (roots: PreConstructionWorksheetTaskTreeNode[]): number =>
  max(roots.map(getPreConstructionWorksheetTaskTreeNodeDepth)) || 0;

export const equalizePreConstructionWorksheetTaskTree = (roots: PreConstructionWorksheetTaskTreeNode[],
                                                         maxDepth: number): void => {
  type NodeWithDepth = { node: PreConstructionWorksheetTaskTreeNode; depth: number };

  const stack: NodeWithDepth[] = roots.map((root) => ({ node: root, depth: 1 }));

  while (stack.length) {
    const { node, depth } = stack.pop()!; // tslint:disable-line:no-non-null-assertion

    if (isEmpty(node.children)) {
      if (depth < maxDepth) {
        node.children = [
          range(maxDepth - depth)
            .reduce(
              (child) => ({
                task: node.task,
                children: child ? [child] : [],
                last: node.last,
                parent: false,
                virtual: true,
              }),
              <PreConstructionWorksheetTaskTreeNode><any>void 0),
        ];
      }
    } else {
      node.children.forEach((childNode) => stack.push({ node: childNode, depth: depth + 1 }));
    }
  }
};

export const getPreConstructionScheduleComplexField = (schedule: PreConstructionWorksheetSchedule, name: string) => {
  const taskId = getPreConstructionWorksheetTaskIdFromName(name);
  if (taskId) {
    const task = schedule.scheduleTasks[taskId];
    return task && task.completionDate;
  } else {
    return schedule[name];
  }
};

export const setPreConstructionScheduleComplexField = (schedule: PreConstructionWorksheetSchedule,
                                                       name: string,
                                                       value: any) => {
  const templateTaskId = getPreConstructionWorksheetTaskIdFromName(name);
  if (templateTaskId) {
    return setNestedEntityField(
      <any>schedule,
      ['scheduleTasks', templateTaskId],
      setEntityFieldWithDirty(schedule.scheduleTasks[templateTaskId], 'completionDate', value));
  } else {
    return setEntityField(schedule, <any>name, value);
  }
};

const getPreConstructionWorksheetTaskTreeNodeDepth = (root: PreConstructionWorksheetTaskTreeNode): number => {
  type NodeWithDepth = { node: PreConstructionWorksheetTaskTreeNode; depth: number };

  const stack: NodeWithDepth[] = [{ node: root, depth: 1 }];

  let maxDepth = 0;

  while (stack.length) {
    const entry = stack.pop()!; // tslint:disable-line:no-non-null-assertion

    if (entry.depth > maxDepth) {
      maxDepth = entry.depth;
    }

    entry.node.children.forEach((node) => stack.push({ node, depth: entry.depth + 1 }));
  }

  return maxDepth;
};
